import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertButton } from '@/components/alert-button';
import {
  AutoEntrepreneurOfferInfoBox,
  LockedOfferInfoBox,
  NegotiationClosedBrandInfoBox,
} from '@/components/offer-status-infobox';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { DealState, LegalStatus } from '@/lib/graphql/generated/graphql';
import { useContainerQuery } from '@/lib/hooks/use-container-query';
import { useUndoDealItems } from '@/lib/hooks/useUndoDealItems';
import { cn } from '@/lib/utils';
import { isMaskableColumn } from '@/lib/utils/column-masking';
import {
  ColumnFilterId,
  useSetColumnFilter,
} from '@/states/column-filters.state';
import { hiddenSortableColumnsState } from '@/states/column-visibility-user.state';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { dealStateState } from '@/states/deal-state.state';
import { filteredRowsCountState } from '@/states/filtered-flat-rows.state';
import { fullscreenState } from '@/states/fullscreen.state';
import { isSidebarAlertOpenState } from '@/states/is-sidebar-alert-open.state';
import { isSidebarFilterOpenState } from '@/states/is-sidebar-filter-open.state';
import { selectedRowsCountState } from '@/states/selected-rows-count.state';
import { undoMutationPendingState } from '@/states/undo-stack.state';
import Button from '@appchoose/button';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Columns3Icon,
  Fullscreen,
  ListFilter,
  Loader2,
  RotateCcw,
  RotateCw,
  Search,
  X,
} from 'lucide-react';

import AnalyzeButton from '../ui-v2/analyze-button';
import ColumnOrderModal from '../ui-v2/column-order-modal';
import DisplayModeButtons from '../ui-v2/displaymode-buttons';

/** Icon-only toolbar (sm, lg, xl): tooltip; md & 2xl show labels. */
function OptionalToolbarTooltip({
  enabled,
  label,
  children,
}: {
  enabled: boolean;
  label: React.ReactNode;
  children: React.ReactElement;
}) {
  if (!enabled) {
    return children;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

export const TableHeader: React.FC = () => {
  const { t } = useTranslation();

  // Atoms
  const [fullscreen, setFullscreen] = useAtom(fullscreenState);
  const [showFilterSidebar, setShowFilterSidebar] = useAtom(
    isSidebarFilterOpenState
  );
  const setShowAlertSidebar = useSetAtom(isSidebarAlertOpenState);
  const user = useUser();
  const selectedRowsCount = useAtomValue(selectedRowsCountState);
  const filteredRowsCount = useAtomValue(filteredRowsCountState);
  const hiddenSortableColumns = useAtomValue(hiddenSortableColumnsState);
  const hasActiveColumnMask = hiddenSortableColumns.some(isMaskableColumn);

  const isInternalUser = user?.isInternalUser;
  const CONTAINER_THRESHOLD = isInternalUser ? 470 : 360;
  const [toolbarContainerRef, isToolbarIconOnly] =
    useContainerQuery<HTMLDivElement>(CONTAINER_THRESHOLD);

  const { getColumnFilter, setColumnFilter } = useSetColumnFilter();
  const dealIdentifier = useAtomValue(dealIdentifierState);
  const dealState = useAtomValue(dealStateState);
  const isOfferLocked = useIsOfferLocked();
  const { deal } = useDealContext();
  const isAutoEntrepreneurOffer =
    deal?.legalStatus === LegalStatus.AutoEntrepreneur;
  const showNegotiationClosedBrandInfobox =
    !isInternalUser && dealState === DealState.NegotiationClosed;
  const showLockedOfferInfobox =
    isOfferLocked && dealState !== DealState.NegotiationClosed;
  const undoPending = useAtomValue(undoMutationPendingState);
  const { canUndo, canRedo, undo, redo } = useUndoDealItems(dealIdentifier);
  const { toast } = useToast();

  const handleUndo = useCallback(async () => {
    const success = await undo();
    if (success) {
      toast({
        title: t('general.success'),
        description: t('components.product_view.undo_success'),
      });
    } else {
      toast({
        title: t('general.error'),
        description: t('components.product_view.undo_error_conflict'),
        variant: 'destructive',
      });
    }
  }, [undo, toast, t]);

  const handleRedo = useCallback(async () => {
    const success = await redo();
    if (success) {
      toast({
        title: t('general.success'),
        description: t('components.product_view.redo_success'),
      });
    } else {
      toast({
        title: t('general.error'),
        description: t('components.product_view.redo_error_conflict'),
        variant: 'destructive',
      });
    }
  }, [redo, toast, t]);

  const [showColumnOrderModal, setShowColumnOrderModal] = useState(false);

  const buttonStyles = isInternalUser
    ? '@header-button-choose:inline'
    : '@header-button-brand:inline';

  return (
    <div>
      {showNegotiationClosedBrandInfobox ? (
        <NegotiationClosedBrandInfoBox />
      ) : showLockedOfferInfobox ? (
        <LockedOfferInfoBox />
      ) : isAutoEntrepreneurOffer ? (
        <AutoEntrepreneurOfferInfoBox />
      ) : null}
      {!showNegotiationClosedBrandInfobox && !isOfferLocked && (
        <AlertButton onClick={() => setShowAlertSidebar(true)} />
      )}

      <div className="flex min-w-0 shrink-0 items-center gap-3 rounded-t-md border-b bg-white py-4 shadow-sm">
        <div className="flex shrink items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder={t('components.other.search_product')}
              value={
                getColumnFilter(ColumnFilterId.__searchTerms).value as string
              }
              onChange={(e) =>
                setColumnFilter(ColumnFilterId.__searchTerms, e.target.value)
              }
              className="h-9 w-52 border-gray-200 bg-gray-50 pl-9 text-sm focus:bg-white"
            />
            {(getColumnFilter(ColumnFilterId.__searchTerms).value as string) !==
              '' && (
              <X
                className="absolute right-2.5 top-2.5 h-4 w-4 cursor-pointer text-gray-500"
                onClick={() =>
                  setColumnFilter(ColumnFilterId.__searchTerms, '')
                }
              />
            )}
          </div>
        </div>

        <div className="min-w-0 flex-[1.6_1_0%]">
          <DisplayModeButtons />
        </div>

        <div className="min-w-0 flex-[1_1_0%]">
          <div ref={toolbarContainerRef} className="w-full @container">
            <TooltipProvider>
              <div className="flex min-w-0 items-center justify-end gap-2">
                {isInternalUser && (
                  <AnalyzeButton showTooltip={isToolbarIconOnly} />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      appearance="outline"
                      containsIcon
                      disabled={!canUndo || isOfferLocked || undoPending}
                      onClick={handleUndo}
                      aria-busy={undoPending}
                      className="box-border flex h-10 items-center gap-2 border border-gray-300 px-3 py-0 font-medium"
                      aria-label={t(
                        'components.product_view.undo_button_tooltip'
                      )}
                    >
                      {undoPending ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        <RotateCcw className="size-4" aria-hidden />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('components.product_view.undo_button_tooltip')}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      appearance="outline"
                      containsIcon
                      disabled={!canRedo || isOfferLocked || undoPending}
                      onClick={handleRedo}
                      aria-busy={undoPending}
                      aria-label={t(
                        'components.product_view.redo_button_tooltip'
                      )}
                      className="box-border flex h-10 items-center gap-2 border border-gray-300 px-3 py-0 font-medium"
                    >
                      {undoPending ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        <RotateCw className="size-4" aria-hidden />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('components.product_view.redo_button_tooltip')}
                  </TooltipContent>
                </Tooltip>
                <OptionalToolbarTooltip
                  enabled={isToolbarIconOnly}
                  label={t('components.filter_sidebar.title')}
                >
                  <Button
                    type="button"
                    onClick={() => setShowFilterSidebar(!showFilterSidebar)}
                    appearance="outline"
                    className="box-border flex h-10 items-center gap-2 border border-gray-300 px-3 py-0 font-medium"
                  >
                    <ListFilter className="size-4 shrink-0" />
                    <span className={`hidden ${buttonStyles}`}>
                      {t('components.filter_sidebar.title')}
                    </span>
                    {filteredRowsCount !== selectedRowsCount && (
                      <div
                        className={`size-2 shrink-0 rounded-full ${
                          filteredRowsCount === 0 &&
                          filteredRowsCount !== selectedRowsCount
                            ? 'bg-red-600'
                            : 'bg-blue-600'
                        }`}
                      />
                    )}
                  </Button>
                </OptionalToolbarTooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      appearance="outline"
                      className="box-border flex h-10 items-center gap-2 border border-gray-300 px-3 py-0 font-medium"
                      onClick={() =>
                        setShowColumnOrderModal(!showColumnOrderModal)
                      }
                    >
                      <div className="relative flex items-center justify-center">
                        <Columns3Icon className="size-4" />
                        {hasActiveColumnMask && (
                          <div className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-blue-600" />
                        )}
                      </div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t('components.column_order_modal.title')}
                  </TooltipContent>
                </Tooltip>
                <ColumnOrderModal
                  open={showColumnOrderModal}
                  onOpenChange={setShowColumnOrderModal}
                />
                <OptionalToolbarTooltip
                  enabled={isToolbarIconOnly}
                  label={
                    !fullscreen
                      ? t('components.fullscreen_button.title.enable')
                      : t('components.fullscreen_button.title.disable')
                  }
                >
                  <Button
                    type="button"
                    appearance={fullscreen ? 'primary' : 'outline'}
                    className={cn(
                      'box-border flex h-10 items-center gap-2 px-3 py-0 font-medium ',
                      !fullscreen && 'border border-gray-300'
                    )}
                    onClick={() => setFullscreen(!fullscreen)}
                  >
                    {!fullscreen ? (
                      <Fullscreen className="size-4 shrink-0" />
                    ) : (
                      <X className="size-4 shrink-0" />
                    )}
                    <span className={`hidden text-nowrap ${buttonStyles}`}>
                      {!fullscreen
                        ? t('components.fullscreen_button.title.enable')
                        : t('components.fullscreen_button.title.disable')}
                    </span>
                  </Button>
                </OptionalToolbarTooltip>
              </div>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
